import { z } from "zod";
import { isoDateTime, paginationQuery, trimmedString, uuidParam } from "./common.js";

/**
 * Appointments, and the visit that records one happened.
 *
 * ── Three ideas, kept apart ─────────────────────────────────────────────────
 *
 * An **appointment** is a plan: somebody decided this work happens at this site,
 * in this window, and named who is doing it. It gets edited - moved, reassigned,
 * retitled - right up until it happens.
 *
 * A **visit** is a record that it did happen. Different writer (the phone, later,
 * offline), different id strategy (a client-generated uuid, so the row exists
 * before the server sees it), and close to append-only, because it is evidence.
 *
 * A **visit type** is what the work turned out to be, from the global
 * `visit_types` table. It hangs off the visit rather than the appointment
 * because a dispatcher books "check the pole at 14 Elm Row" and whether that is
 * a splice or a survey is knowable on arrival and nowhere earlier.
 */

/* ────────────────────────── Reference lists ────────────────────────────── */

/**
 * Which categorical colour the calendar draws a work type in.
 *
 * A token key, never a hex - `docs/02-design-system.md`, and the generated
 * `tokens.css` is the only place a colour value exists.
 *
 * **Deliberately not the severity ramp.** `advisory` / `caution` / `stop` are
 * spoken for, and a "Pole replacement" type rendered in caution amber sitting
 * next to a genuine Caution hazard chip is the exact collision decision #8 was
 * shaped to avoid. These six hues exist so a type can have a colour without
 * borrowing a meaning.
 */
export const APPOINTMENT_COLORS = ["cat-1", "cat-2", "cat-3", "cat-4", "cat-5", "cat-6"] as const;

export type AppointmentColor = (typeof APPOINTMENT_COLORS)[number];

/**
 * `GET /visit-types` and `GET /assessment-modules`.
 *
 * Both are read-only and **unpaged**. They are global reference lists of a dozen
 * rows that every client loads once and holds; paging them would make two
 * clients implement a cursor over something that fits in a dropdown. There is no
 * create or update schema either - `scripts/bootstrap.ts` writes them, and
 * editing them from the console is its own feature (decisions.md #39).
 */
export const listReferenceQuery = z.object({
  is_active: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
});
export type ListReferenceQuery = z.infer<typeof listReferenceQuery>;

/* ───────────────────────────── Appointments ───────────────────────────── */

export const APPOINTMENT_STATUSES = ["scheduled", "in_progress", "completed", "cancelled"] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

export const APPOINTMENT_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type AppointmentPriority = (typeof APPOINTMENT_PRIORITIES)[number];

/**
 * How urgent, as a word. Mirrored by `kPriorityLabels` in
 * `mobile-app/lib/core/models/scheduling.dart`.
 *
 * Here rather than in a component because three surfaces render it - the chip,
 * the agenda row and the detail panel - and a priority that reads "Urgent" in
 * one and "urgent" in another is the kind of drift the two-clients rule exists
 * to catch.
 */
export const PRIORITY_LABELS: Record<AppointmentPriority, string> = {
  low: "Low",
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

/* ─────────────────── why priority has no colour map ─────────────────────
 *
 * There is no priority colour map any more, and its absence is the decision.
 *
 * Priority used to be mapped onto `cat-1` .. `cat-6`, which was always flagged
 * as a stopgap. It was worse than a stopgap: those six hues are chosen to be
 * maximally different **from each other**, which is the one property an ordered
 * property must not have. `high` landed on olive and `normal` on cyan, so the
 * calendar drew high-priority work calmer than routine work. A scale that reads
 * backwards is worse than no scale at all.
 *
 * So the clients key a ramp off the priority value itself - `.event--prio-high`
 * in the console, `priorityTone` on the phone. The ramp is **quiet → plain →
 * warm → loud**, and loudness is carried by fill, left-bar width and type weight
 * together rather than by hue alone, so it survives a reader who cannot separate
 * the two warm steps.
 *
 * `low` and `normal` are deliberately close. "Is this slightly less routine than
 * that" is not a question anybody asks a calendar; "is anything on fire" is, and
 * the warm end of the ramp is spent entirely on answering it.
 *
 * The severity ramp stays off limits for priority. The one carve-out is not a
 * priority at all: a **missed** appointment takes `caution`, because it is an
 * exception state rather than a grade of routine work - and it carries a dashed
 * edge and the word "Missed" beside the hue, which is the same bargain
 * `SeverityChip` makes.
 * ────────────────────────────────────────────────────────────────────────── */

const latitude = z.coerce.number().min(-90).max(90);
const longitude = z.coerce.number().min(-180).max(180);

/**
 * No work type. What kind of work a job turns out to be is recorded on the
 * visit, against `visit_types`, because the technician does not know it until
 * they arrive.
 */
const appointmentFields = {
  title: trimmedString(2, 160),
  description: z.string().trim().max(2000).nullable().optional(),
  /**
   * The customer's own work-order number, when there is one. Unique per company
   * so a Phase 5 integration can upsert on it; absent is the normal case,
   * because TaskIQ has to be fully usable with no work-order system at all
   * (PRD §4, requirement 10).
   */
  reference: z.string().trim().max(120).nullable().optional(),
  customer_name: z.string().trim().max(160).nullable().optional(),
  /**
   * Where the *work* is, which is not the site.
   *
   * A site is a crew, depot or yard. The job is at a customer address possibly
   * thirty miles away, and PRD §3.1 is explicit that it changes every couple of
   * hours. Filing both on one row is what lets a supervisor ask "which yard is
   * this crew's" and "where did they actually go" separately.
   */
  location_address: z.string().trim().max(400).nullable().optional(),
  location_lat: latitude.nullable().optional(),
  location_lng: longitude.nullable().optional(),
  scheduled_start: isoDateTime,
  scheduled_end: isoDateTime,
  /** Null is a real state - unassigned work is the dispatcher's inbox. */
  assigned_to: uuidParam.nullable().optional(),
  priority: z.enum(APPOINTMENT_PRIORITIES).default("normal"),
  notes: z.string().trim().max(2000).nullable().optional(),
};

/** A window has to be a window. Mirrored by a check constraint on the table. */
const windowOrdered = <T extends { scheduled_start?: string; scheduled_end?: string }>(
  value: T,
  ctx: z.RefinementCtx
) => {
  if (!value.scheduled_start || !value.scheduled_end) return;
  if (Date.parse(value.scheduled_end) <= Date.parse(value.scheduled_start)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["scheduled_end"],
      message: "The end time has to be after the start time",
    });
  }
};

export const createAppointmentSchema = z.object(appointmentFields).superRefine(windowOrdered);
export type CreateAppointmentInput = z.infer<typeof createAppointmentSchema>;

/**
 * `site_id` is absent on purpose: it travels in the path on create
 * (`POST /sites/:siteId/appointments`, decisions #20) and is immutable once a
 * visit exists, because moving an appointment between sites would re-tenant the
 * evidence attached to it. Same argument as `updateSiteSchema` omitting
 * `company_id`.
 *
 * `status` accepts only the two values a human decides. `in_progress` and
 * `completed` are written by `app.sync_appointment_status` when a visit appears,
 * so offering them here would be two sources of truth for one fact.
 */
export const updateAppointmentSchema = z
  .object(appointmentFields)
  .partial()
  .extend({
    status: z.enum(["scheduled", "cancelled"]).optional(),
    cancellation_reason: z.string().trim().max(500).nullable().optional(),
  })
  .superRefine(windowOrdered);
export type UpdateAppointmentInput = z.infer<typeof updateAppointmentSchema>;

/**
 * How many appointments one request may carry, and what it gets when it does
 * not say.
 *
 * This is the one list in the product whose natural unit is a **window** rather
 * than a page. A month grid is not showing you page one of August; it is either
 * showing August or it is lying, and a client that has to stitch four requests
 * together to draw one calendar has moved a server concern into a render loop.
 * So the default is the ceiling, and the ceiling lives here rather than in a
 * `pageSize=` the console types into every calendar request - a number the
 * clients each copy is a number they eventually disagree about.
 *
 * It is not unbounded. `MAX_RANGE_DAYS` in the controller caps the span at 62
 * days, so the realistic worst case is two months of one site's work, and a
 * thousand rows of that is a busy depot rather than a scan of the table.
 *
 * Callers that genuinely want a page still send one - the agenda view asks for
 * ten, the unassigned tray for fifty, and the dashboard tiles for one row they
 * only read `total` off.
 */
export const MAX_APPOINTMENT_PAGE_SIZE = 1000;

/**
 * `GET /sites/:siteId/appointments`.
 *
 * `from` and `to` are **instants, not days**. "The week of 17 August at North
 * Yard" is a different instant range in America/Chicago than in UTC, and the
 * client is the only party that knows which zone the reader is looking at. It
 * computes the range from the site's timezone and sends the boundaries.
 *
 * `pageSize` overrides `paginationQuery`'s 25/100 rather than extending it -
 * see `MAX_APPOINTMENT_PAGE_SIZE` for why a calendar is not a paged list.
 *
 * **`search` is omitted, and its absence is the design.** A text filter over a
 * grid produces a calendar with holes in it: the same screen that says "nothing
 * on Thursday" when Thursday is full. Finding a job by reference is a lookup,
 * and it belongs on the visits table - which keeps its `search` - or on a global
 * search, not on the surface whose whole job is to be a complete picture of a
 * window. docs/decisions.md #32.
 */
export const listAppointmentsQuery = paginationQuery.omit({ search: true }).extend({
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_APPOINTMENT_PAGE_SIZE)
    .default(MAX_APPOINTMENT_PAGE_SIZE),
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
  status: z.enum(APPOINTMENT_STATUSES).optional(),
  assigned_to: uuidParam.optional(),
  /** Mutually exclusive with `assigned_to`; the controller ignores the pair. */
  unassigned: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
});
export type ListAppointmentsQuery = z.infer<typeof listAppointmentsQuery>;

/**
 * `GET /sites/:siteId/appointments/agenda` - the home screen's agenda card, on
 * both clients. Same instant-not-day rule as above: `todayStart`/`tomorrowStart`
 * are the site's own day boundary, computed by the client. `now` and the
 * overdue/upcoming windows around it are the API's own business - see the
 * controller.
 */
export const agendaQuery = z.object({
  todayStart: isoDateTime,
  tomorrowStart: isoDateTime,
  limit: z.coerce.number().int().min(1).max(20).default(5),
});
export type AgendaQuery = z.infer<typeof agendaQuery>;

/**
 * `GET /sites/:siteId/summary` - the home screen's count tiles, on both clients.
 *
 * One request for figures that were five, for the same reason `agenda` is one
 * request rather than one per tab: they are read together, refreshed together,
 * and five in flight means five moments for the row to settle.
 *
 * Same division of labour as everything else here - the client sends the site's
 * own day boundary and the API derives the windows around it (a week back for
 * visits, a fortnight forward for unassigned work), so the horizon is defined
 * once instead of twice in two languages.
 */
export const siteSummaryQuery = z.object({
  todayStart: isoDateTime,
  tomorrowStart: isoDateTime,
});
export type SiteSummaryQuery = z.infer<typeof siteSummaryQuery>;

/**
 * `GET /sites/:siteId/insights` - the Overview page's compliance charts, and the
 * phone's Compliance card.
 *
 * Same two parameters as `siteSummaryQuery` and the same division of labour: the
 * client sends the site's own day boundary, and every window behind it - a
 * fortnight of volume, eight weeks of compliance, the meters' own period - is
 * the API's business, defined once instead of twice in two languages.
 *
 * Deliberately no window parameter. The card subtitles on both clients name
 * their windows in prose, and a period the caller could widen is a period those
 * subtitles would start lying about.
 */
export const siteInsightsQuery = z.object({
  todayStart: isoDateTime,
  tomorrowStart: isoDateTime,
});
export type SiteInsightsQuery = z.infer<typeof siteInsightsQuery>;

/* ──────────────────────────────── Visits ──────────────────────────────── */

export const VISIT_STATUSES = ["in_progress", "completed", "abandoned"] as const;
export type VisitStatus = (typeof VISIT_STATUSES)[number];

/** Supervisor triage. The console's only write against a visit (#36). */
export const REVIEW_STATES = ["open", "flagged", "reviewed"] as const;
export type ReviewState = (typeof REVIEW_STATES)[number];

/** Both clients say the same three words. */
export const REVIEW_STATE_LABELS: Record<ReviewState, string> = {
  open: "Not reviewed",
  flagged: "Flagged",
  reviewed: "Reviewed",
};

/**
 * `POST /appointments/:id/visit` - the technician arrives and starts work.
 *
 * This opens a visit and its pending module rows in one request, because a
 * technician standing at a pole should not be able to end up with a visit that
 * has nothing to do. There is no `user_id`: whoever calls this is who attended,
 * which is also what makes the assessment rows insertable - the trigger copies
 * `user_id` onto them and the insert policy requires it to be the caller.
 *
 * Every id is supplied by the caller and is a primary key, which is what makes
 * the whole call safe to retry after the ambiguous failure - request delivered,
 * response lost - the normal outcome on a rural pole line rather than an edge
 * case.
 */
export const openVisitSchema = z.object({
  id: uuidParam,
  /**
   * What the work turned out to be, only knowable here: the appointment
   * carries no type, because the technician finds out on arrival. Absent or
   * null means "Other" - the catalogue is not exhaustive, and forcing a pick
   * teaches the least-wrong entry, which poisons every per-type number.
   */
  visit_type_id: uuidParam.nullable().optional(),
  started_at: isoDateTime.optional(),
  /** Filled by the capture flow only. Null from anything else, deliberately. */
  arrival_lat: latitude.nullable().optional(),
  arrival_lng: longitude.nullable().optional(),
  /**
   * The modules the technician chose, each with its own id. At least one - a
   * visit with no assessment is a visit that recorded nothing.
   */
  modules: z
    .array(z.object({ id: uuidParam, module_id: uuidParam }))
    .min(1)
    .max(12),
});
export type OpenVisitInput = z.infer<typeof openVisitSchema>;

/**
 * `PATCH /visits/:id` - two callers, two disjoint jobs.
 *
 * The technician closes their own visit; an administrator triages it with
 * `review_state`. They are refused in the same body rather than split into two
 * routes because it is one row and one set of guards - and
 * `app.enforce_visit_write_rules` is the authority on who may touch which
 * column, so restating it in Express would be a second copy that drifts.
 */
export const updateVisitSchema = z
  .object({
    status: z.enum(["completed", "abandoned"]).optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
    review_state: z.enum(REVIEW_STATES).optional(),
  })
  .refine((body) => Object.keys(body).length > 0, { message: "Nothing to change" })
  .refine(
    (body) =>
      body.review_state === undefined || (body.status === undefined && body.notes === undefined),
    { message: "Reviewing a visit and closing it are separate requests" }
  );
export type UpdateVisitInput = z.infer<typeof updateVisitSchema>;

/**
 * `DELETE /appointments/:id`, which cancels.
 *
 * A body on a DELETE is unusual and deliberate: the reason belongs to the act of
 * cancelling, and putting it in a query string would make it the only piece of
 * free text in the API that travels in a URL and lands in an access log.
 */
export const cancelAppointmentSchema = z.object({
  cancellation_reason: z.string().trim().max(500).nullable().optional(),
});
export type CancelAppointmentInput = z.infer<typeof cancelAppointmentSchema>;

export const listVisitsQuery = paginationQuery.extend({
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
  status: z.enum(VISIT_STATUSES).optional(),
  visit_type_id: uuidParam.optional(),
  user_id: uuidParam.optional(),
  review_state: z.enum(REVIEW_STATES).optional(),
});
export type ListVisitsQuery = z.infer<typeof listVisitsQuery>;

/**
 * The visits page's tiles: the list's filter set minus paging, because the
 * tiles must count the same rows the list under them shows - all of them, not
 * the visible page (decisions #40).
 */
export const visitStatsQuery = listVisitsQuery.omit({ page: true, pageSize: true });
export type VisitStatsQuery = z.infer<typeof visitStatsQuery>;
